'use client';

import type { ReactNode } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { InlineToolExpand } from './InlineToolExpand';
import { Trace } from './trace';
import {
  headlineToolName,
  stoppedHeadlineLabel,
  type ChatHeadlineKind,
} from './chat-headline-actions';
import { useHeadlineCopy } from './chat-headline-copy';

export function wrapChildNode({
  key,
  rendered,
}: {
  key: string;
  rendered: ReactNode;
}): ReactNode {
  return (
    <Box key={key} width="100%" minW={0} _empty={{ display: 'none' }}>
      {rendered}
    </Box>
  );
}

export function wrapNestedChildren(childContent: ReactNode[]): ReactNode {
  if (childContent.length === 0) {
    return undefined;
  }

  return (
    <Trace nested>
      <Box pr={2} width="100%">
        <VStack alignItems="stretch" gap="6px" width="100%">
          {childContent}
        </VStack>
      </Box>
    </Trace>
  );
}

function HeadlineActivity({
  kind,
  nestedContent,
  nestedSummary,
  isRunning,
  isStopped,
  hasFailedCreation,
  forceExpanded,
  defaultExpanded,
  expansionKey,
}: {
  kind: ChatHeadlineKind;
  nestedContent?: ReactNode;
  nestedSummary?: string;
  isRunning: boolean;
  isStopped: boolean;
  hasFailedCreation: boolean;
  forceExpanded: boolean;
  defaultExpanded: boolean;
  expansionKey: string;
}): ReactNode {
  const copy = useHeadlineCopy(kind);
  const displayText = isRunning
    ? copy.running
    : isStopped
      ? stoppedHeadlineLabel(copy.running)
      : hasFailedCreation && copy.failed
        ? copy.failed
        : copy.done;

  return (
    <InlineToolExpand
      toolName={headlineToolName(kind)}
      displayText={displayText}
      status={
        isRunning
          ? 'in-progress'
          : isStopped
            ? 'aborted'
            : hasFailedCreation
              ? 'failed'
              : 'completed'
      }
      isSubAgent
      forceExpanded={forceExpanded}
      defaultExpanded={defaultExpanded}
      nestedContent={nestedContent}
      summary={nestedSummary}
      expansionKey={expansionKey}
      headlineKind={kind}
    />
  );
}

export function renderHeadlineActivity({
  kind,
  nestedContent,
  nestedSummary,
  isRunning,
  isStopped = false,
  hasFailedCreation = false,
  forceExpanded,
  defaultExpanded = false,
  expansionKey,
}: {
  kind: ChatHeadlineKind;
  nestedContent?: ReactNode;
  nestedSummary?: string;
  isRunning: boolean;
  isStopped?: boolean;
  hasFailedCreation?: boolean;
  forceExpanded: boolean;
  defaultExpanded?: boolean;
  expansionKey: string;
}): ReactNode {
  return (
    <HeadlineActivity
      kind={kind}
      nestedContent={nestedContent}
      nestedSummary={nestedSummary}
      isRunning={isRunning}
      isStopped={isStopped}
      hasFailedCreation={hasFailedCreation}
      forceExpanded={forceExpanded}
      defaultExpanded={defaultExpanded}
      expansionKey={expansionKey}
    />
  );
}
